import { DEFAULT_EVENT_SETTINGS, detectCurrentEvent } from '../config/events.js';

// Konstanten für LocalStorage Keys
const KEYS = {
    ADMIN_SETTINGS: 'tef-admin-settings',
    SPIELGELD_SETTINGS: 'tef-spielgeld-settings',
    ADMIN_THEME: 'tef-admin-theme',
    PAGE_LAYOUT: 'tef-page-layout',
};

class StateService {
    constructor() {
        this.listeners = new Set();
        this.bc = null;

        this.initBroadcastChannel();
        this.initStorageListener();
        
        // Asynchron initiales Setup vom Server laden
        setTimeout(() => this.loadSettingsFromServer(), 0);
    }

    // Initialisiert den Broadcast Channel für Live-Sync (gleicher Origin)
    initBroadcastChannel() {
        try {
            this.bc = new BroadcastChannel('tef-settings-sync');
            this.bc.onmessage = this.handleBroadcastMessage.bind(this);
        } catch (e) {
            console.warn('StateService: BroadcastChannel wird nicht unterstützt', e);
        }
    }

    // Initialisiert den Storage Event Listener für Cross-Tab Sync (Fallback)
    initStorageListener() {
        window.addEventListener('storage', (e) => {
            if (e.key === KEYS.ADMIN_SETTINGS && e.newValue) {
                try {
                    const parsed = JSON.parse(e.newValue);
                    if (detectCurrentEvent) parsed.activeEvent = detectCurrentEvent();
                    const fresh = { ...DEFAULT_EVENT_SETTINGS, ...parsed };
                    this.notifyListeners(fresh, 'adminSettings');
                } catch (err) { /* ignore parse errors */ }
            } else if (e.key === KEYS.SPIELGELD_SETTINGS && e.newValue) {
                try {
                    const fresh = JSON.parse(e.newValue);
                    this.notifyListeners(fresh, 'spielgeldSettings');
                } catch (err) { }
            } else if (e.key === KEYS.PAGE_LAYOUT && e.newValue) {
                try {
                    const fresh = JSON.parse(e.newValue);
                    this.notifyListeners(fresh, 'pageLayout');
                } catch (err) { }
            }
        });
    }

    // Behandelt eingehende Nachrichten von anderen Tabs (Frontend / Admin)
    handleBroadcastMessage(ev) {
        if (ev.data?.type === 'tef-settings-update' && ev.data.settings) {
            this.notifyListeners(ev.data.settings, 'adminSettings');
        } else if (ev.data?.type === 'tef-spielgeld-update' && ev.data.settings) {
            this.notifyListeners(ev.data.settings, 'spielgeldSettings');
        } else if (ev.data?.type === 'tef-page-layout-update' && ev.data.layout) {
            this.notifyListeners(ev.data.layout, 'pageLayout');
        } else if (ev.data?.type === 'tef-builder-select-widget') {
            this.notifyListeners(ev.data.widgetId, 'activeWidgetId');
        }
    }

    // --- Abonnieren von Änderungen ---
    subscribe(callback) {
        this.listeners.add(callback);
        return () => this.listeners.delete(callback);
    }

    notifyListeners(data, type) {
        this.listeners.forEach(callback => callback(data, type));
    }

    // --- Admin Settings (Event, Design, Effekte) ---
    getAdminSettings() {
        try {
            const raw = localStorage.getItem(KEYS.ADMIN_SETTINGS);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (detectCurrentEvent) parsed.activeEvent = detectCurrentEvent();
                return { ...DEFAULT_EVENT_SETTINGS, ...parsed };
            }
        } catch (e) {
            console.error('StateService: Fehler beim Laden der Admin-Settings', e);
        }
        return { ...DEFAULT_EVENT_SETTINGS };
    }

    saveAdminSettings(settings, broadcast = true) {
        try {
            localStorage.setItem(KEYS.ADMIN_SETTINGS, JSON.stringify(settings));

            this.notifyListeners(settings, 'adminSettings');

            // Legacy Events für alte Frontend-Komponenten
            window.dispatchEvent(new CustomEvent('tef-settings-changed', { detail: settings }));

            if (broadcast && this.bc) {
                this.bc.postMessage({ type: 'tef-settings-update', settings });
            }
        } catch (e) {
            console.error('StateService: Fehler beim Speichern der Admin-Settings', e);
        }
    }

    async loadSettingsFromServer() {
        try {
            const res = await fetch('/api/settings');
            if (res.ok) {
                const data = await res.json();
                if (Object.keys(data).length > 0) {
                    const merged = { ...DEFAULT_EVENT_SETTINGS, ...data };
                    localStorage.setItem(KEYS.ADMIN_SETTINGS, JSON.stringify(merged));
                    this.notifyListeners(merged, 'adminSettings');
                }
            }
        } catch(e) {
            console.warn('StateService: Konnte Server-Settings nicht laden (Fallback auf LocalStorage)', e);
        }
    }

    // --- Spielgeld Settings ---
    getSpielgeldSettings(defaultSettings = {}) {
        try {
            const raw = localStorage.getItem(KEYS.SPIELGELD_SETTINGS);
            if (raw) return { ...defaultSettings, ...JSON.parse(raw) };
        } catch (e) {
            console.error('StateService: Fehler beim Laden der Spielgeld-Settings', e);
        }
        return { ...defaultSettings };
    }

    saveSpielgeldSettings(settings) {
        try {
            localStorage.setItem(KEYS.SPIELGELD_SETTINGS, JSON.stringify(settings));

            this.notifyListeners(settings, 'spielgeldSettings');

            // Legacy Event
            window.dispatchEvent(new CustomEvent('tef-spielgeld-settings-changed', { detail: { spielgeldSettings: settings } }));

            if (this.bc) {
                this.bc.postMessage({ type: 'tef-spielgeld-update', settings });
            }
        } catch (e) {
            console.error('StateService: Fehler beim Speichern der Spielgeld-Settings', e);
        }
    }

    // --- Backend API (Layouts) ---
    async fetchPublicPage(id) {
        try {
            const res = await fetch(`/api/public-page/${id}`);
            if (res.ok) {
                return await res.json();
            }
            if (res.status === 404) {
                return null;
            }
            throw new Error(`Public Page API Error: ${res.status}`);
        } catch (e) {
            console.error('StateService: Fehler beim Laden der öffentlichen Seite', e);
            return null;
        }
    }

    async fetchPublicTemplate(id) {
        if (!id) return null;
        try {
            const res = await fetch(`/api/public-page/template/${id}`);
            if (res.ok) {
                return await res.json();
            }
            return null;
        } catch (e) {
            console.error('StateService: Fehler beim Laden des öffentlichen Templates', e);
            return null;
        }
    }

    async fetchSavedLayouts() {
        try {
            const token = localStorage.getItem('tef_jwt');
            const res = await fetch('/api/layouts', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.status === 401 || res.status === 403) {
                // Token ungültig/abgelaufen → Auth-Expired Event auslösen (kein harter Redirect)
                window.dispatchEvent(new CustomEvent('tef:auth-expired', { detail: { source: 'fetchSavedLayouts' } }));
                return [];
            }
            if (res.ok) {
                return await res.json();
            }
        } catch (e) {
            console.warn('StateService: API offline oder Fehler beim Laden, nutze Fallback', e);
        }
        // Fallback LocalStorage
        try {
            const raw = localStorage.getItem('tef-saved-layouts');
            if (raw) return JSON.parse(raw);
        } catch (e) {}
        return [];
    }

    async saveLayoutToDB(layoutConfig) {
        try {
            const token = localStorage.getItem('tef_jwt');
            const res = await fetch('/api/layouts', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(layoutConfig)
            });
            if (res.status === 401 || res.status === 403) {
                window.dispatchEvent(new CustomEvent('tef:auth-expired', { detail: { source: 'saveLayoutToDB' } }));
                throw new Error('Unauthorized');
            }
            if (!res.ok) throw new Error('Server Return war fehlerhaft');
            
            // Halte LocalStorage sync für Fallback
            this._updateFallbackStorage(layoutConfig);
            return await res.json();
        } catch (e) {
            console.error('StateService: API Fehler beim Speichern, Fallback LocalStorage', e);
            this._updateFallbackStorage(layoutConfig);
            return { success: false, fallback: true };
        }
    }

    async deleteLayoutFromDB(layoutId) {
        try {
            const token = localStorage.getItem('tef_jwt');
            const res = await fetch(`/api/layouts/${layoutId}`, { 
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.status === 401 || res.status === 403) {
                window.dispatchEvent(new CustomEvent('tef:auth-expired', { detail: { source: 'deleteLayoutFromDB' } }));
                throw new Error('Unauthorized');
            }
            this._deleteFallbackStorage(layoutId);
            return true;
        } catch (e) {
            console.error('StateService: API Fehler beim Löschen, Fallback', e);
            this._deleteFallbackStorage(layoutId);
            return false;
        }
    }

    _updateFallbackStorage(entry) {
        try {
            const raw = localStorage.getItem('tef-saved-layouts');
            const current = raw ? JSON.parse(raw) : [];
            const index = current.findIndex(l => l.id === entry.id);
            if (index >= 0) current[index] = entry;
            else current.push(entry);
            localStorage.setItem('tef-saved-layouts', JSON.stringify(current));
        } catch(e) {}
    }

    _deleteFallbackStorage(layoutId) {
        try {
            const raw = localStorage.getItem('tef-saved-layouts');
            const current = raw ? JSON.parse(raw) : [];
            const updated = current.filter(l => l.id !== layoutId);
            localStorage.setItem('tef-saved-layouts', JSON.stringify(updated));
        } catch(e) {}
    }

    // --- Page Layout (Builder) ---
    getPageLayout() {
        try {
            const raw = localStorage.getItem(KEYS.PAGE_LAYOUT);
            if (raw) return JSON.parse(raw);
        } catch (e) {
            console.error('StateService: Fehler beim Laden des Page Layouts', e);
        }
        // Default Layout if none exists: Header -> Grid
        return [
            { id: 'default-header', type: 'Header', props: {} },
            { id: 'default-grid', type: 'TariffGrid', props: {} }
        ];
    }

    savePageLayout(layout) {
        try {
            localStorage.setItem(KEYS.PAGE_LAYOUT, JSON.stringify(layout));
            this.notifyListeners(layout, 'pageLayout');

            if (this.bc) {
                this.bc.postMessage({ type: 'tef-page-layout-update', layout });
            }
        } catch (e) {
            console.error('StateService: Fehler beim Speichern des Page Layouts', e);
        }
    }

    // Für Cross-Frame Kommunikation: Widget in Canvas ausgewählt (wird nicht persistiert)
    selectWidget(widgetId) {
        this.notifyListeners(widgetId, 'activeWidgetId');
        if (this.bc) {
            this.bc.postMessage({ type: 'tef-builder-select-widget', widgetId });
        }
    }

    // ==============================================================
    // --- SPIELGELD RECHNER API (PostgreSQL / SQLite Backend) ---
    // ==============================================================

    async fetchSessions() {
        try {
            const res = await fetch('/api/sessions');
            if (res.ok) {
                const sessions = await res.json();
                this.notifyListeners(sessions, 'spielgeldSessions');
                return sessions;
            }
            throw new Error(`API Error: ${res.status}`);
        } catch (e) {
            console.error('StateService: Fehler beim Laden der Sessions', e);
            // Error Logging
            this.logError('Laden der Sessions fehlgeschlagen', e.message);
            return []; // Safe Fallback
        }
    }

    async saveSession(sessionData) {
        try {
            const res = await fetch('/api/sessions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(sessionData)
            });
            if (!res.ok) throw new Error('Server Fehler beim Speichern');
            
            // Broadcast the update so all tabs/components refresh
            const result = await res.json();
            if (this.bc) this.bc.postMessage({ type: 'tef-sessions-updated' });
            return result.session;
        } catch (e) {
            console.error('StateService: API Fehler beim Speichern der Session', e);
            this.logError('Speichern der Session fehlgeschlagen', e.message);
            return null;
        }
    }

    async deleteSession(sessionId) {
        try {
            const res = await fetch(`/api/sessions/${sessionId}`, { method: 'DELETE' });
            if (!res.ok) throw new Error('Server Fehler beim Löschen');
            
            if (this.bc) this.bc.postMessage({ type: 'tef-sessions-updated' });
            return true;
        } catch (e) {
            console.error('StateService: API Fehler beim Löschen der Session', e);
            this.logError('Löschen der Session fehlgeschlagen', e.message);
            return false;
        }
    }

    // Lädt Tarife, Smartphones etc. aus der DB (Katalog-Daten)
    async fetchCatalogData(endpoint) {
        try {
            const res = await fetch(`/api/${endpoint}`);
            if (res.ok) return await res.json();
            return [];
        } catch (e) {
            console.error(`StateService: Fehler beim Laden von /api/${endpoint}`, e);
            return [];
        }
    }

    // Fehler-Logging (Safe Fallback) ans CMS Backend
    async logError(summary, details) {
        try {
            const token = localStorage.getItem('tef_jwt');
            await fetch('/api/errors', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    type: 'js_error',
                    severity: 'medium',
                    summary: summary,
                    message: details,
                    source: 'StateService/SpielgeldRechner'
                })
            });
        } catch (e) {
            // Silently ignore if logging itself fails
        }
    }
}

// Singleton-Export
export const stateService = new StateService();
